import React, { useEffect, useMemo, useState } from "react";

import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

interface TemplateVersion {
  revisionId: string;
  label: string;
}

interface TemplateComparisonProps {
  templateVersions: TemplateVersion[];
  getTemplateData: (versionId: string) => Promise<string>;
  isDarkTheme?: boolean;
}

const TemplateComparison: React.FC<TemplateComparisonProps> = ({
  templateVersions,
  getTemplateData,
  isDarkTheme = false,
}) => {
  const [leftVersion, setLeftVersion] = useState("");
  const [rightVersion, setRightVersion] = useState("");
  const [leftData, setLeftData] = useState("");
  const [rightData, setRightData] = useState("");
  const [isLoadingLeft, setIsLoadingLeft] = useState(false);
  const [isLoadingRight, setIsLoadingRight] = useState(false);
  const [showDiffOnly, setShowDiffOnly] = useState(true);

  // Set default selections when versions are available
  useEffect(() => {
    if (templateVersions.length >= 2 && !leftVersion && !rightVersion) {
      setLeftVersion(templateVersions[1].revisionId);
      setRightVersion(templateVersions[0].revisionId);
    }
  }, [templateVersions, leftVersion, rightVersion]);

  // Load left version data
  useEffect(() => {
    if (leftVersion) {
      setIsLoadingLeft(true);
      getTemplateData(leftVersion)
        .then(setLeftData)
        .catch(() => setLeftData(""))
        .finally(() => setIsLoadingLeft(false));
    }
  }, [leftVersion, getTemplateData]);

  // Load right version data
  useEffect(() => {
    if (rightVersion) {
      setIsLoadingRight(true);
      getTemplateData(rightVersion)
        .then(setRightData)
        .catch(() => setRightData(""))
        .finally(() => setIsLoadingRight(false));
    }
  }, [rightVersion, getTemplateData]);

  const handleLeftVersionChange = (event: SelectChangeEvent) => {
    setLeftVersion(event.target.value);
  };

  const handleRightVersionChange = (event: SelectChangeEvent) => {
    setRightVersion(event.target.value);
  };

  // Template size thresholds (in MB) - matching doc-search.tsx
  const TEMPLATE_SIZE_THRESHOLDS = {
    GOOD: 3,
    WARNING: 4,
    ERROR: 5,
    MAX_RECOMMENDED: 5,
  } as const;

  // Size conversion constants (matching doc-search.tsx)
  const BYTES_PER_MB = 1024 * 1024;
  const BYTES_PER_KB = 1024;
  const SIZE_DECIMAL_PLACES = 2;

  // JSON size calculation functions (same as in doc-search.tsx)
  const getJsonSizeInMB = (json: string): string => {
    const bytes = new TextEncoder().encode(JSON.stringify(json)).length;
    const megabytes = bytes / BYTES_PER_MB;
    return megabytes.toFixed(SIZE_DECIMAL_PLACES);
  };

  const getJsonSizeInKB = (json: string): string => {
    const bytes = new TextEncoder().encode(JSON.stringify(json)).length;
    const kilobytes = bytes / BYTES_PER_KB;
    return kilobytes.toFixed(SIZE_DECIMAL_PLACES);
  };

  type ChipColor = "success" | "warning" | "error";
  const getChipColor = (sizeInMB: number): ChipColor => {
    // Template size thresholds
    if (sizeInMB < TEMPLATE_SIZE_THRESHOLDS.GOOD) return "success";
    if (sizeInMB < TEMPLATE_SIZE_THRESHOLDS.WARNING) return "warning";
    return "error";
  };

  const renderSizeChip = (data: string) => {
    if (!data) return null;

    const sizeInMB = parseFloat(getJsonSizeInMB(data));
    const sizeInKB = getJsonSizeInKB(data);
    const chip = (
      <Chip
        label={`${sizeInMB} MB`}
        color={getChipColor(sizeInMB)}
        size="small"
        style={{ textAlign: "right" }}
      />
    );
    const tooltipText = `${sizeInKB} kB`;

    return (
      <Tooltip placement="top" title={tooltipText}>
        {chip}
      </Tooltip>
    );
  };

  // JSON formatting constants
  const JSON_INDENT_SPACES = 2;

  // UI constants
  const DIFF_VIEW_MIN_HEIGHT = 400;

  // Format JSON for better diff visualization
  const formatJsonForDiff = (jsonString: string): string => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, JSON_INDENT_SPACES);
    } catch (error) {
      return jsonString;
    }
  };

  // Prepare data for diff viewer
  const { leftFormattedData, rightFormattedData, leftLabel, rightLabel } =
    useMemo(() => {
      const leftLabel =
        templateVersions.find((v) => v.revisionId === leftVersion)?.label ||
        "Left Version";
      const rightLabel =
        templateVersions.find((v) => v.revisionId === rightVersion)?.label ||
        "Right Version";

      return {
        leftFormattedData: leftData ? formatJsonForDiff(leftData) : "",
        rightFormattedData: rightData ? formatJsonForDiff(rightData) : "",
        leftLabel,
        rightLabel,
      };
    }, [leftData, rightData, leftVersion, rightVersion, templateVersions]);

  const renderDiffView = () => {
    if (isLoadingLeft || isLoadingRight) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={DIFF_VIEW_MIN_HEIGHT}
        >
          <Typography>Loading template data for comparison...</Typography>
        </Box>
      );
    }

    if (!leftData || !rightData) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={DIFF_VIEW_MIN_HEIGHT}
        >
          <Typography>
            {!leftData && !rightData
              ? "No data available for comparison"
              : `Missing data for ${!leftData ? leftLabel : rightLabel}`}
          </Typography>
        </Box>
      );
    }

    try {
      // Validate both JSON strings
      JSON.parse(leftData);
      JSON.parse(rightData);

      return (
        <ReactDiffViewer
          oldValue={leftFormattedData}
          newValue={rightFormattedData}
          splitView={true}
          compareMethod={DiffMethod.WORDS}
          leftTitle={leftLabel}
          rightTitle={rightLabel}
          useDarkTheme={isDarkTheme}
          hideLineNumbers={false}
          showDiffOnly={showDiffOnly}
          styles={{
            variables: {
              light: {
                codeFoldGutterBackground: "#f7f7f7",
                codeFoldBackground: "#f1f8ff",
              },
            },
            contentText: {
              fontSize: "12px",
              lineHeight: "1.4",
            },
          }}
        />
      );
    } catch (error) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={DIFF_VIEW_MIN_HEIGHT}
        >
          <Typography color="error">
            Invalid JSON data. Cannot compare templates with malformed JSON.
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Template Version Comparison</Typography>
        <Tooltip
          title={showDiffOnly ? "Expand All" : "Collapse to Diffs"}
          placement="left"
        >
          <IconButton
            onClick={() => setShowDiffOnly(!showDiffOnly)}
            data-testid="diff-mode-toggle"
            size="small"
          >
            {showDiffOnly ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Left Version</InputLabel>
              <Select
                value={leftVersion}
                label="Left Version"
                onChange={handleLeftVersionChange}
                data-testid="left-version-select"
              >
                {templateVersions.map((version) => (
                  <MenuItem
                    key={`left-${version.revisionId}`}
                    value={version.revisionId}
                    disabled={version.revisionId === rightVersion}
                  >
                    {version.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderSizeChip(leftData)}
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Right Version</InputLabel>
              <Select
                value={rightVersion}
                label="Right Version"
                onChange={handleRightVersionChange}
                data-testid="right-version-select"
              >
                {templateVersions.map((version) => (
                  <MenuItem
                    key={`right-${version.revisionId}`}
                    value={version.revisionId}
                    disabled={version.revisionId === leftVersion}
                  >
                    {version.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderSizeChip(rightData)}
          </Box>
        </Grid>
      </Grid>

      <Paper
        sx={{
          padding: 2,
          minHeight: 400,
          border: "1px solid #e0e0e0",
          "& .react-diff-viewer": {
            fontFamily: "monospace",
          },
        }}
      >
        {renderDiffView()}
      </Paper>
    </Box>
  );
};

export default TemplateComparison;
